// React
import React, { useCallback, useRef, useState, useEffect, useMemo, createContext, useContext, useLayoutEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
// Component library
import Chip, { CHIP_TYPE } from "@acadia-ui/chip";
import DataTable, { TD_TYPE, ColumnTemplate, CELL_STATE } from "@acadia-ui/datatable";
import { helpers } from "@acadia-ui/theme";
import AppSDK from "@growthos/ui-app-sdk";
import Badge from "@acadia-ui/badge";
import Checkbox, { TreeNode } from "@acadia-ui/checkbox";
import Button, { BUTTON_DISPLAY, BUTTON_SIZE } from "@acadia-ui/button";
import Textbox, { TEXTBOX_SIZE } from "@acadia-ui/textbox";
import Status, { STATUS_TYPE } from "@acadia-ui/status";
import { PlatformStatusIcon } from "../../../Components/Partials/Icons/PlatformStatusIcon";

// Custom components
import ActionBar from "../ActionBar";
import { ARCHIVE_REFRESH_REQUESTED_ORIGINATOR, ViewMasterCampaignsContext, rowOptions } from "../index";
import { useNewPrimaryCampaignAction, CampaignDownloadAction } from "../actions";
import { FiltersSidesheet } from "../FiltersSidesheet";

// Actions
import { useNewActivationCampaignAction } from "../actions";
import { getCampaignDownloads } from "../../../Flux/Actions/MasterCampaignActions";

// Stores
import MasterCampaignsStore from "../../../Flux/Stores/MasterCampaignsStore";

// Utils
import {
	// this comment exists to prevent eslint from making this unreadable.
	doSearch,
	doFilter,
	masterCampaignNameCellRenderer,
	sortDataForClonedCampaign,
	highlightClonedCampaign,
	resetCloneHighlighting,
	getCrossPlatformCampaign,
	handleNavigateToWorkbook,
	customDateRenderer,
	TaxonomyCellRenderer,
	highlightClonedActivationCampaign,
	CustomDataProvider,
	hideAccordionControlRenderer
} from "../utils";
import EmptyState from "@acadia-ui/empty-state";
import Heading, { HEADING_LEVEL, HEADING_SIZE } from "@acadia-ui/heading";

const MODAL_RENDER_CONTAINER_ID = "actionBarRenderContainer";

export const DataTableContext = createContext();

export default () => {
	const customDateButtonportalLocation = document.querySelector("#overview-filter-date ul");

	const location = useLocation();
	const history = useNavigate();
	const aarc = AppSDK.getContext();

	const [loading, setLoading] = useState(false);
	const [hasDraft, setHasDraft] = useState(false);

	// Context from index.jsx (api calls mostly inform these values)
	const vmcc = useContext(ViewMasterCampaignsContext);
	const {
		// This comment exists to prevent eslint from making this unreadable.
		// data,
		// loading,
		refreshData: refreshDataRaw,
		platforms,
		clonedCampaignData,
		enabledPlatformAPIs,
		selectedTab,
		// dataKeyDataValueFilters,
		setActions,
		archiveRefreshRequested,
		setArchiveRefreshRequested,
		// isCrossPlatform,
		// setIsCrossPlatform
		isUSCurrency
	} = vmcc;

	const dataTableApi = useRef();
	const [dataProvider, setDataProvider] = useState(null);

	useLayoutEffect(() => {
		setTimeout(() => {
			if (!loading) {
				dataProvider?.resetExpandedAccordionsVisually?.();
			}
		}, 1);
	}, [dataProvider, loading]);

	useEffect(() => {
		if (selectedTab === 0) {
			if (!dataProvider) {
				const dp = new CustomDataProvider({
					id: "overview-table",
					data: [],
					totalRows: 0,
					currentPage: 0,
					childDataProviders: [],
					archive: false,
					requestData: async (...args) => {
						dataTableApi?.current?.element?.current?.querySelector?.(".gos-dt-table")?.scrollTo?.({ top: 0, behavior: "instant" });
						setLoading(true);
						const response = await refreshDataRaw(...args);
						if (!response?.data && response?.name === "AbortError") {
							// Specifically when the response is an AbortError, we don't want to set loading to false
						} else {
							setHasDraft(response?.showDraftStatusColumn ?? false);

							setLoading(false);
							return response;
						}
					},
					updateCheckboxes: () => {
						dataTableApi?.current?.rowSelectionManager?.setTree(CustomDataProvider.getCustomTree("overview-table", dataTableApi));
						setTimeout(() => {
							updateNumSelected();
						}, 1);
					}
				});

				setDataProvider(dp);
			} else if (archiveRefreshRequested === ARCHIVE_REFRESH_REQUESTED_ORIGINATOR.ARCHIVE_TAB) {
				dataProvider.update();
				setArchiveRefreshRequested(ARCHIVE_REFRESH_REQUESTED_ORIGINATOR.NONE);
			}
		}
	}, [dataProvider, selectedTab, location, dataTableApi, archiveRefreshRequested]);

	useEffect(() => {
		if (!dataProvider) {
			return;
		}
		const urlParams = new URLSearchParams(location.search);
		const taxonomy = urlParams.get("taxonomy");

		if (taxonomy) {
			dataProvider?.search?.(taxonomy);
			// Clear the taxonomy param from URL after using it
			urlParams.delete("taxonomy");
			const newSearch = urlParams.toString();
			history({ pathname: location.pathname, search: newSearch ? `?${newSearch}` : "" }, { replace: true });
		}
	}, [location, dataProvider]);

	// action hooks to be rendered
	const NewActivationCampaignAction = useNewActivationCampaignAction();
	const NewPrimaryCampaignAction = useNewPrimaryCampaignAction();

	// filter the platforms based on which ones are actually available in the ActivationCampaigns data set
	const availablePlatforms = useMemo(() => {
		return platforms;
		// TODO: ask if we can nix this filtering logic since we're paginating now. else, we will need to handle it via api call
		// if (platforms && data) {
		// 	return platforms?.filter?.((platform) => {
		// 		return data?.some?.(({ ActivationCampaigns }) => {
		// 			return Object.values(ActivationCampaigns)?.some((platformCampaigns) => {
		// 				return platformCampaigns?.some(({ MasterPlatformID }) => MasterPlatformID == platform.MasterPlatformID);
		// 			});
		// 		});
		// 	});
		// }
		// return [];
	}, [platforms /* , data */]);

	const enabledPlatformIds = useMemo(() => {
		return enabledPlatformAPIs?.filter(({ SyncStatus }) => SyncStatus == true)?.map(({ MasterPlatformID }) => MasterPlatformID?.toString()) || [];
	}, [enabledPlatformAPIs]);

	// quickfilters passed in actionBarProps
	const quickFilters = useMemo(() => {
		if (availablePlatforms) {
			const platformOptions = availablePlatforms.map(({ label, MasterPlatformID }) => (
				<Checkbox id={`overview-platform-${MasterPlatformID}`} value={MasterPlatformID}>
					{label}
				</Checkbox>
			));

			const dateListItems = [
				<Checkbox id="overview-filter-last-30-days" value="last-30-days">
					Last 30 Days
				</Checkbox>,
				<Checkbox id="overview-filter-this-month" value="this-month">
					This Month
				</Checkbox>,
				<Checkbox id="overview-filter-this-year" value="this-year">
					This Year
				</Checkbox>
			];

			return [
				<Chip type={CHIP_TYPE.LIST} id="overview-filter-platform-name" listItems={platformOptions} isClearable>
					Activation Platform
				</Chip>,
				<Chip type={CHIP_TYPE.LIST} id="overview-filter-date" listItems={dateListItems} isClearable>
					Date
				</Chip>
			];
		}
		return null;
	}, [availablePlatforms]);

	const [selectedActivationCampaigns, setSelectedActivationCampaigns] = useState([]);
	const [selectedMasterCampaigns, setSelectedMasterCampaigns] = useState([]);

	const updateNumSelected = () => {
		const tree = dataTableApi?.current?.rowSelectionManager?.tree;
		const masterCampaignCheckboxes = tree?.children?.loadedNodes;

		// if select all is enabled, use the totalCount from dataprovider
		setTimeout(() => {
			// prettier-ignore
			dataTableApi?.current?.rowSelectionManager?.setNumRowsSelected(
                Math.max(
                    TreeNode.getNodeCount(tree, true, false),
                    TreeNode.getNodeCount(tree, true, true)
                )
            );
		}, 100);

		setSelectedActivationCampaigns((pac) => {
			const newActivationCampaigns = [];

			setSelectedMasterCampaigns((pmc) => {
				return masterCampaignCheckboxes?.reduce((selectedCampaigns, masterCampaignCheckbox, i) => {
					if (masterCampaignCheckbox.selectionState === true) {
						if (masterCampaignCheckbox.renderProps?.value) {
							selectedCampaigns.push(masterCampaignCheckbox.renderProps?.value);
						}
					}

					const activationCampaignCheckboxes = masterCampaignCheckbox?.children?.loadedNodes;
					const childData = dataTableApi?.current?.dataProvider?.childDataProviders?.[i]?.data;

					if (activationCampaignCheckboxes?.length > 0) {
						activationCampaignCheckboxes?.forEach(({ selectionState, renderProps, value, ...rest }, i) => {
							if (selectionState === true) {
								const childIndex = value?.split("-group-")?.[1] || -1;
								if (childIndex !== -1) {
									const { PlatformName, CampaignID } = childData?.[childIndex] || {};
									newActivationCampaigns.push({
										masterCampaignId: masterCampaignCheckbox.renderProps?.value,
										platform: PlatformName,
										activationCampaignId: CampaignID
									});
								}
							}
						});
					}

					return selectedCampaigns;
				}, []);
			});
			return newActivationCampaigns;
		});
	};

	const numSelectedFormatter = useCallback(() => {
		let message = [];
		const allSelected = dataTableApi?.current?.rowSelectionManager?.tree?.selectionState === true;

		if (selectedMasterCampaigns?.length == 1) {
			message.push(1);
			message.push(` primary campaign fully selected`);
		} else {
			message.push(allSelected ? dataProvider.totalPrimaryCampaignsCount : selectedMasterCampaigns?.length);
			message.push(` primary campaigns fully selected`);
		}

		let primaryCampaignsPartiallySelected;

		if (allSelected) {
			primaryCampaignsPartiallySelected = [];
		} else {
			primaryCampaignsPartiallySelected = Array.from(
				new Set(
					selectedActivationCampaigns
						?.filter(({ masterCampaignId }) => {
							return !selectedMasterCampaigns?.includes(masterCampaignId);
						})
						?.map(({ masterCampaignId }) => masterCampaignId)
				)
			);
		}

		if (primaryCampaignsPartiallySelected?.length == 1) {
			message = [
				...message,
				`, and `,
				1,
				` primary campaign partially selected; which together include `,
				selectedActivationCampaigns?.length,
				` selected activation campaign`
			];
		} else {
			message = [
				...message,
				`, and `,
				primaryCampaignsPartiallySelected?.length,
				` primary campaigns partially selected; which together include `,
				allSelected ? dataProvider.totalActivationCampaignsCount : selectedActivationCampaigns?.length,
				` selected activation campaign`
			];
		}

		if (selectedActivationCampaigns?.length !== 1) {
			message.push(`s`);
		}

		message.push(`.`);

		// use reduce to return a formatted message react fragment where all digits are bolded
		return message.reduce(
			(acc, curr) => {
				if (typeof curr === "number") {
					return (
						<>
							{" "}
							{acc} <Badge>{curr}</Badge>
						</>
					);
				}
				return (
					<>
						{acc}
						{curr}
					</>
				);
			},
			<></>
		);
	}, [selectedActivationCampaigns, selectedMasterCampaigns]);

	// clone highlighting effect (manipulates the dom directly)
	// useEffect(() => {
	// 	if (!loading && clonedCampaignData && MasterCampaignsStore?.getClonedActivationCampaign()) {
	// 		setTimeout(() => {
	// 			const { clonedActivationCampaign, targetMasterCampaignID, previousActivationCampaignIDs } = clonedCampaignData;

	// 			//find the platform name
	// 			const platformName = platforms.find(({ MasterPlatformID }) => MasterPlatformID == clonedActivationCampaign?.MasterPlatformID)?.PlatformName;

	// 			//find the activation campaigns on the required platform\
	// 			const platformCampaigns = data.find(({ MasterCampaignID }) => MasterCampaignID == targetMasterCampaignID)?.ActivationCampaigns?.[platformName];

	// 			//find the ID of the new campaign
	// 			const campaignId = platformCampaigns?.find(({ CampaignID }) => !previousActivationCampaignIDs?.includes(CampaignID))?.CampaignID;

	// 			//use these parameters to perform highlighting on the DOM
	// 			highlightClonedCampaign(campaignId, platformName);
	// 		}, 1000);
	// 	}
	// }, [data, loading, clonedCampaignData]);

	const columns = [
		<ColumnTemplate
			key="MasterCampaignUniqueIDCode"
			bind="MasterCampaignUniqueIDCode"
			type={TD_TYPE.ACCORDION_CONTROL}
			minWidth={true}
			hasRenderCache={false}
			// onRender={(_id, row) => hideAccordionControlRenderer(dataProvider, row)}
		></ColumnTemplate>,

		<ColumnTemplate bind="CampaignID" type={TD_TYPE.CHECKBOX} key="CampaignID">
			Select
		</ColumnTemplate>,
		...(hasDraft
			? [
					<ColumnTemplate
						bind="IsDraft"
						key="IsDraftColumn"
						onRender={(value, rowData) => (value === "True" ? <Status type={STATUS_TYPE.WARNING} title={"Draft"} /> : <></>)}
						minWidth={true}
					>
						Draft Status
					</ColumnTemplate>
				]
			: []),
		<ColumnTemplate
			isSortable
			type={TD_TYPE?.TEXT}
			key={"CampaignName_Friendly"}
			bind={"CampaignName_Friendly"}
			columnSettings={{ label: "Campaign Name", canHide: false, defaultHidden: false }}
			onRender={(id, rowData) => {
				if (rowData?.ParentUniqueIDcode) {
					return (
						<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
							<TaxonomyCellRenderer taxonomy={rowData?.CampaignName} isPrisma={rowData?.MasterPlatformID === "5"} />
							{rowData?.CampaignName_Friendly}
						</div>
					);
				}
				return masterCampaignNameCellRenderer(rowData, aarc);
			}}
		>
			Campaign name
		</ColumnTemplate>,
		<ColumnTemplate
			// This comment exists to prevent eslint from making this unreadable.
			type={TD_TYPE?.TEXT}
			isSortable
			key={"PlatformName"}
			bind={"PlatformName"}
			columnSettings={{ label: "Activation Platform", canHide: true, defaultHidden: false }}
		>
			Activation platform
		</ColumnTemplate>,
		<ColumnTemplate
			type={TD_TYPE.NONE}
			isSortable
			key={"StartDate"}
			bind={"StartDate"}
			onRender={(value, rowData) => <>{rowData?.ParentUniqueIDcode && customDateRenderer(value, rowData)}</>}
			columnSettings={{ label: "Start Date", canHide: true, defaultHidden: false }}
		>
			Start date
		</ColumnTemplate>,
		<ColumnTemplate
			type={TD_TYPE.NONE}
			isSortable
			key={"EndDate"}
			bind={"EndDate"}
			onRender={(value, rowData) => <>{rowData?.ParentUniqueIDcode && customDateRenderer(value, rowData)}</>}
			columnSettings={{ label: "End Date", canHide: true, defaultHidden: false }}
		>
			End date
		</ColumnTemplate>,
		<ColumnTemplate
			isSortable
			key={"Budget"}
			bind={"Budget"}
			type={TD_TYPE.NONE}
			onRender={(value, rowData) => {
				if (value && rowData?.ParentUniqueIDcode) {
					const formattedValue = isUSCurrency
						? helpers.formatCurrency(value)
						: parseFloat(value).toLocaleString(undefined, {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2
							});
					if (rowData?.IsPrismaBudgetLocked == "True") {
						return (
							<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
								<i className="icon-lock" />
								{formattedValue}
							</div>
						);
					}
					return <>{formattedValue}</>;
				}
				return <></>;
			}}
			columnSettings={{ label: "Budget", canHide: true, defaultHidden: false }}
		>
			Budget
		</ColumnTemplate>,
		<ColumnTemplate
			type={TD_TYPE?.NONE}
			key={"Status"}
			bind={"Status"}
			onRender={(value, rowData) => {
				if (rowData?.ParentUniqueIDcode) {
					return (
						<PlatformStatusIcon
							status={enabledPlatformIds.includes(rowData?.MasterPlatformID) ? parseInt(rowData?.ExternalSystemStatusID, 10) : 5}
							tooltip={enabledPlatformIds.includes(rowData?.MasterPlatformID) ? rowData?.ExternalSystemResponse || rowData?.ExternalSystemStatusName : "Not eligible"}
							isBadge={false}
						/>
					);
				}
				return <></>;
			}}
			columnSettings={{ label: "Status", canHide: true }}
		>
			Status
		</ColumnTemplate>
	];

	useEffect(() => {
		setActions(
			selectedTab === 0 ? (
				<>
					<CampaignDownloadAction />
					<NewPrimaryCampaignAction.Trigger />
				</>
			) : (
				<></>
			)
		);
	}, [selectedTab]);

	useEffect(() => {
		if (loading === false && MasterCampaignsStore.getTargetMasterCampaignID()) {
			setTimeout(() => {
				highlightClonedCampaign(MasterCampaignsStore.getTargetMasterCampaignID());
				MasterCampaignsStore.resetClonedActivationCampaign();
			}, 1000);
		}
	}, [loading]);

	// render!
	return (
		<DataTableContext.Provider
			value={{
				// should remove dataTableApi from CheckboxSelections in the future, keeping for backwards compatibility now.
				checkboxSelections: { selectedActivationCampaigns, selectedMasterCampaigns, dataTableApi },
				dataProvider,
				dataTableApi,
				loading,
				setLoading,
				refreshData: () => {
					dataTableApi?.current?.update();
				}
			}}
		>
			{/* this container is where all modals are rendered. ReactDOM.createPortal() adds children here */}
			<div id={MODAL_RENDER_CONTAINER_ID}>
				<NewActivationCampaignAction.DisplayedComponent dataProvider={dataProvider} />
				<NewPrimaryCampaignAction.DisplayedComponent dataProvider={dataProvider} />
			</div>
			{/* portal a special button into the date quickfilter menu. we can't just render it there directly. */}
			{customDateButtonportalLocation &&
				createPortal(
					<li tabIndex={-1}>
						<Button
							// this comment exists to prevent eslint from making this unreadable.
							id="custom-date-button"
							display={BUTTON_DISPLAY.TEXT}
							size={BUTTON_SIZE.SMALL}
							onClick={() => {
								dataTableApi?.current?.toolbarApi?.current?.setFiltersSideSheetIsOpen?.(true);
							}}
							data-tracking-id-alt="open-custom-date-filters-side-sheet"
						>
							Custom Date
						</Button>
					</li>,
					customDateButtonportalLocation
				)}

			<DataTable
				ref={dataTableApi}
				id={"overview-table"}
				bindKey={"CampaignID"}
				onCheckboxChange={updateNumSelected}
				emptyState={
					<EmptyState
						heading={
							<Heading size={HEADING_SIZE.MEDIUM} as={HEADING_LEVEL.H2} description={"There is no data to display."}>
								No Data
							</Heading>
						}
						icon={"icon-info"}
					/>
				}
				onClickRow={(e, rowData) => {
					if (
						rowData?.ParentUniqueIDcode &&
						!e?.target?.parentElement?.classList?.contains?.("gos-chk") &&
						!e?.target?.parentElement?.classList?.contains?.("gos-popover__trigger") &&
						e?.target?.parentElement?.tagName !== "BUTTON"
					) {
						handleNavigateToWorkbook(e, rowData, history);
					}
					if (clonedCampaignData && e?.target?.parentElement?.tagName === "BUTTON" && clonedCampaignData?.targetMasterCampaignID === rowData.MasterCampaignID) {
						setTimeout(() => {
							highlightClonedActivationCampaign(clonedCampaignData?.clonedActivationCampaign);
						}, 2500);
					}
				}}
				actionBarProps={{
					search: (
						<Textbox
							debounceTimeout={500}
							size={TEXTBOX_SIZE.SMALL}
							onClear={(e) => {
								setLoading(true);
								dataProvider?.search("");
							}}
							value={new URLSearchParams(location.search)?.get?.("taxonomy") || undefined}
						>
							Search
						</Textbox>
					),
					filters: {
						fullFilters: [],
						quickFilters
					},
					actions: [<ActionBar renderContainer={document.getElementById(MODAL_RENDER_CONTAINER_ID)} />],
					filtersSideSheet: (
						<FiltersSidesheet
							prefix={"overview"}
							availablePlatforms={availablePlatforms || []}
							hasDraft={hasDraft}
							onSave={(filters) => {
								dataProvider?.filter(filters);
							}}
						/>
					),
					settingsSideSheet: true,
					numSelectedFormatter,
					showEditMode: false
				}}
				data={dataProvider}
				paginationProps={{ rowOptions }}
				hasVirtualScrolling={false}
				isLoading={loading}
			>
				{columns}
			</DataTable>
		</DataTableContext.Provider>
	);
};
